import React, { Fragment, ReactElement } from 'react';
import useStyles from './styles';
import { Control, DeepMap, FieldError, useForm } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import { Translations } from './configuration';
import {
  AppSelect,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Button, Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import * as VatReturnListSelector from 'redux/reducers/vatReturn_list_reducer/selectors';
import VatReturnListActions from 'redux/reducers/vatReturn_list_reducer/actions';
import CentralBankPaymentStatus from 'config/centralBankPayments';
import useQuery from 'custom_hooks/useQuery';
import { TableDefaultFetchValues } from '../../configuration';
import CentralBankPaymentActions from 'redux/reducers/payment_central_bank_reducer/actions';
import ModalActions from '../../../../redux/reducers/gl_modal_reducer/actions';
import { ModalIDs } from '../../../../redux/reducers/gl_modal_reducer/types';
import { fil } from 'date-fns/locale';

interface IProps {
  id: number;
  amount: number;
  date: string;
}

interface FormFields {
  vatReturnId: string | null;
  filter: string | null;
}

export default function MatchCentralBankPayment(props: IProps): ReactElement {
  const dispatch = useDispatch();

  const defaultValues = {
    vatReturnId: null,
    filter: null,
  };

  const { errors, control, handleSubmit, watch } = useForm<FormFields>({
    defaultValues,
  });

  interface VatReturnListAllData {
    vat_return_id: number;
    vat_return_ref: string;
  }
  interface VatReturnListAllOutput {
    data: VatReturnListAllData[] | null;
  }

  React.useEffect(() => {
    dispatch(VatReturnListActions.fetchVatReturnListAllAction());
  }, [dispatch]);

  const classes = useStyles();
  const watchVatReturnId = watch('vatReturnId');

  const query = useQuery();

  const sortField: string = query.get('sortField')
    ? (query.get('sortField') as string)
    : TableDefaultFetchValues.TABLE_DEFAULT_SORT_KEY;
  const page: number = query.get('page')
    ? Number(query.get('page'))
    : TableDefaultFetchValues.TABLE_DEFAULT_PAGE;
  const pageSize: number = query.get('pageSize')
    ? Number(query.get('pageSize'))
    : TableDefaultFetchValues.TABLE_DEFAULT_PAGE_SIZE;
  const order: 'asc' | 'desc' = query.get('order')
    ? (query.get('order') as 'asc' | 'desc')
    : TableDefaultFetchValues.TABLE_DEFAULT_ORDER;

  const account = query.get('account');
  const statusCode = query.get('statusCode');
  const dateFrom = query.get('dateFrom');
  const dateTo = query.get('dateTo');

  const temp: VatReturnListAllOutput = { data: [] };
  const vatReturn = VatReturnListSelector.useGetVatReturnListAll();
  const [vatReturnValues, setVatReturnValues] = React.useState(temp);

  React.useEffect(() => {
    setVatReturnValues(vatReturn);
  }, [vatReturn]);

  const filter = watch('filter');

  React.useEffect(() => {
    if (filter) {
      const data: VatReturnListAllOutput = {
        data: (vatReturn.data ? vatReturn.data : []).filter((x) =>
          x.vat_return_ref.includes(filter)
        ),
      };
      setVatReturnValues(data);
    } else {
      setVatReturnValues(vatReturn);
    }
  }, [filter]);

  const onSubmit = (data: any) => {
    dispatch(
      VatReturnListActions.addVatReturnPaymentAction({
        vat_return_id: watchVatReturnId ? watchVatReturnId : '',
        amount: props.amount,
        payment_date: new Date(props.date),
        onSuccess: () => {
          dispatch(
            CentralBankPaymentActions.updateCentralBankPaymentStatus({
              id: props.id,
              statusCode: CentralBankPaymentStatus.PROCESSED_OK,
              onSuccess: () => {
                dispatch(
                  CentralBankPaymentActions.setCentralBankPaymentsAction({
                    sortField: sortField,
                    page: page,
                    pageSize: pageSize,
                    order: order,
                    account: account,
                    statusCode: statusCode,
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                  })
                );
              },
            })
          );
        },
        onError: () => {
          dispatch(
            CentralBankPaymentActions.updateCentralBankPaymentStatus({
              id: props.id,
              statusCode: CentralBankPaymentStatus.PROCESSED_WITH_ERRORS,
              onSuccess: () => {
                dispatch(
                  CentralBankPaymentActions.setCentralBankPaymentsAction({
                    sortField: sortField,
                    page: page,
                    pageSize: pageSize,
                    order: order,
                    account: account,
                    statusCode: statusCode,
                    dateFrom: dateFrom,
                    dateTo: dateTo,
                  })
                );
              },
            })
          );
        },
      })
    );
    dispatch(
      ModalActions.closeModalAction({
        modalID: ModalIDs.MODAL_MATCH_CENTRAL_BANK_PAYMENT,
      })
    );
  };

  return (
    <form className={classes.form} noValidate onSubmit={handleSubmit(onSubmit)}>
      <Box width={500}>
        <GridLayout
          elements={[
            {
              element: (
                <AppTextField
                  variant="outlined"
                  fullWidth
                  id="filter"
                  name="filter"
                  label="Filter Vat Return"
                  placeholder="Filter Vat Return..."
                  control={control}
                  rules={{}}
                />
              ),
              size: 12,
              id: 'test',
            },
            {
              element: (
                <AppSelect
                  variant="outlined"
                  required
                  fullWidth
                  id="vatReturnId"
                  label={<TranslatedText textMap={Translations.vat_returns} />}
                  error={'vatReturnId' in errors}
                  name="vatReturnId"
                  control={control}
                  rules={{ required: 'Required Field' }}
                  options={(vatReturnValues.data
                    ? vatReturnValues.data
                    : []
                  ).map((x) => {
                    return {
                      id: `${x.vat_return_id}`,
                      label: x.vat_return_ref,
                    };
                  })}
                  helperText={errors.vatReturnId && errors.vatReturnId.message}
                />
              ),
              size: 12,
              id: 'vatReturnId',
            },
            {
              element: (
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    width: '100%',
                    marginTop: '0.5rem',
                    marginBottom: '1rem',
                  }}
                  type="submit"
                >
                  <TranslatedText textMap={Translations.match_payment} />
                </Button>
              ),
              size: 12,
              id: 'match',
            },
          ]}
        />{' '}
      </Box>
    </form>
  );
}
